/**
 * Electrical meaning of a single-line drawing: which points each protective device feeds,
 * which RCDs protect it, and what is not connected. Derived from the wiring every time —
 * the drawing is the source of truth.
 */
import type { Circuit, Element, Id, Point, Project, Sheet, Wire } from '@elec/domain';
import { distanceToSegment, wirePolyline } from '@elec/renderer';
import { getSymbol, portPosition, registerProjectSymbols } from '@elec/symbols';

export const PROTECTIVE_DEVICES = new Set([
  'iec.breaker',
  'iec.rcbo',
  'iec.fuse',
  'iec.breaker-2p',
  'iec.breaker-3p',
  'iec.breaker-4p',
  'iec.fuse-3p',
]);
export const RCD_DEVICES = new Set(['iec.rcd', 'iec.rcd-2p', 'iec.rcd-4p', 'iec.rcbo']);
const SOCKETS = new Set(['iec.socket', 'iec.socket-noearth', 'iec.socket-3p']);

export type PointKind = 'socket' | 'light' | 'appliance' | 'command' | 'other';

export function pointKind(el: Element): PointKind | null {
  const def = getSymbol(el.symbolId);
  if (!def) return null;
  if (SOCKETS.has(def.id)) return 'socket';
  switch (def.category) {
    case 'outlet':
      return 'other'; // telecom / TV outlets: not power points
    case 'lighting':
      return 'light';
    case 'appliance':
    case 'motor':
      return 'appliance';
    case 'command':
      return 'command';
    case 'signalling':
      return 'other';
    default:
      return null;
  }
}

/** Points counted by the RGIE "8 points per circuit" rule: sockets and lighting points. */
export const isCountedPoint = (k: PointKind) => k === 'socket' || k === 'light';

const eps = 1e-3;
const posKey = (p: Point) => `x${Math.round(p.x * 100)},${Math.round(p.y * 100)}`;
const portKey = (elementId: Id, portId: string) => `p${elementId}:${portId}`;

class UnionFind {
  private parent = new Map<string, string>();
  find(a: string): string {
    let p = this.parent.get(a) ?? a;
    if (p !== a) {
      p = this.find(p);
      this.parent.set(a, p);
    }
    return p;
  }
  union(a: string, b: string) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(ra, rb);
  }
}

export interface Connectivity {
  /** Net id of an element port. */
  netOf(elementId: Id, portId: string): string;
  /** Ports in a net. */
  portsIn(net: string): Array<{ elementId: Id; portId: string }>;
  /** Wires in a net. */
  wiresIn(net: string): Wire[];
  /** Net of a wire. */
  netOfWire(wireId: Id): string | undefined;
}

/** Pairing key of an off-page connector: its link name, within its group (generated sheets). */
export function offpageKey(el: Element): string {
  const link = String(el.props.link ?? '').trim();
  return link ? `${String(el.props.group ?? '')}|${link}` : '';
}

/**
 * Union-find over ports and wire geometry for one sheet, or several sheets joined by off-page
 * connectors (same link name). Crossing wires do not connect; T-junctions do.
 */
export function connectivity(project: Project, sheets: Id | readonly Id[]): Connectivity {
  const uf = new UnionFind();
  const sheetSet = new Set(typeof sheets === 'string' ? [sheets] : sheets);
  const wires = Object.values(project.wires).filter((w) => sheetSet.has(w.sheetId));
  const polylines: Array<{ w: Wire; pts: Point[]; a: string; b: string }> = [];
  for (const w of wires) {
    const pts = wirePolyline(project, w);
    if (!pts || pts.length < 2) continue;
    const at = (p: Point) => `${w.sheetId}/${posKey(p)}`;
    const endKey = (e: Wire['from'], p: Point) =>
      e.kind === 'port' ? portKey(e.elementId, e.portId) : at(p);
    const a = endKey(w.from, pts[0]!);
    const b = endKey(w.to, pts.at(-1)!);
    uf.union(a, b);
    // A port and the point it sits on are the same node (wires drawn to a port position).
    uf.union(a, at(pts[0]!));
    uf.union(b, at(pts.at(-1)!));
    polylines.push({ w, pts, a, b });
  }
  // Ports: register their positions so a free wire end placed exactly on a port connects.
  const ports: Array<{ elementId: Id; portId: string }> = [];
  const links = new Map<string, string>();
  for (const el of Object.values(project.elements)) {
    if (!sheetSet.has(el.sheetId)) continue;
    const def = getSymbol(el.symbolId);
    if (!def) continue;
    for (const port of def.ports) {
      ports.push({ elementId: el.id, portId: port.id });
      uf.union(portKey(el.id, port.id), `${el.sheetId}/${posKey(portPosition(def, el, port))}`);
    }
    // Off-page connectors with the same link name are one node.
    const link = el.symbolId === 'iec.offpage' ? offpageKey(el) : '';
    if (link) {
      const first = links.get(link);
      if (first) uf.union(first, portKey(el.id, 'a'));
      else links.set(link, portKey(el.id, 'a'));
    }
  }
  // T-junctions: a wire end on another wire's run (same sheet).
  for (const pl of polylines) {
    for (const [end, key] of [
      [pl.pts[0]!, pl.a],
      [pl.pts.at(-1)!, pl.b],
    ] as const) {
      for (const other of polylines) {
        if (other === pl || other.w.sheetId !== pl.w.sheetId) continue;
        for (let i = 0; i < other.pts.length - 1; i++) {
          if (distanceToSegment(end, other.pts[i]!, other.pts[i + 1]!) < eps) {
            uf.union(key, other.a);
            break;
          }
        }
      }
    }
  }
  const byNet = new Map<string, Array<{ elementId: Id; portId: string }>>();
  for (const p of ports) {
    const n = uf.find(portKey(p.elementId, p.portId));
    byNet.set(n, [...(byNet.get(n) ?? []), p]);
  }
  const wiresByNet = new Map<string, Wire[]>();
  for (const pl of polylines) {
    const n = uf.find(pl.a);
    wiresByNet.set(n, [...(wiresByNet.get(n) ?? []), pl.w]);
  }
  return {
    netOf: (e, p) => uf.find(portKey(e, p)),
    portsIn: (n) => byNet.get(n) ?? [],
    wiresIn: (n) => wiresByNet.get(n) ?? [],
    netOfWire: (id) => {
      const pl = polylines.find((x) => x.w.id === id);
      return pl ? uf.find(pl.a) : undefined;
    },
  };
}

export interface CircuitPoint {
  element: Element;
  kind: PointKind;
}

export interface CircuitInfo {
  /** The protective device (breaker, RCBO, fuse). */
  device: Element;
  circuit: Circuit | undefined;
  /** Circuit code (letter), '' if the device is not linked to a circuit yet. */
  code: string;
  sheet: Sheet;
  /** Points fed by this device, top-to-bottom. */
  points: CircuitPoint[];
  /** RCDs upstream, nearest first (an RCBO protects itself and comes first). */
  rcds: Element[];
  /** Cable reference from the circuit table, or from a wire label such as "XVB 3G2,5". */
  cable: { type: string; sectionMm2: number; cores: number; source: 'table' | 'label' | 'none' };
  ratingA: number | undefined;
}

export interface Analysis {
  circuits: CircuitInfo[];
  /** Points on single-line sheets that no protective device feeds. */
  unprotected: Element[];
  /** Elements on single-line sheets with no wire on any port. */
  unconnected: Element[];
  /** Circuits downstream of each RCD id. */
  byRcd: Map<Id, CircuitInfo[]>;
}

const CABLE_RE = /(\d+)\s*[Gx×]\s*(\d+(?:[.,]\d+)?)/i;

export function parseCable(
  label: string | undefined,
): { type: string; cores: number; sectionMm2: number } | null {
  if (!label) return null;
  const m = CABLE_RE.exec(label);
  if (!m) return null;
  const type = label.slice(0, m.index).trim();
  return { type, cores: Number(m[1]), sectionMm2: Number(m[2]!.replace(',', '.')) };
}

const num = (v: unknown) =>
  typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? Number(v) : undefined;

export function analyse(project: Project): Analysis {
  registerProjectSymbols(project.symbols);
  const circuits: CircuitInfo[] = [];
  const unprotected: Element[] = [];
  const unconnected: Element[] = [];
  const byRcd = new Map<Id, CircuitInfo[]>();

  // One network over all single-line sheets: a board continued on a second sheet (off-page
  // connectors) is still one board.
  const singleLine = Object.values(project.sheets).filter((s) => s.kind === 'single-line');
  const net = connectivity(
    project,
    singleLine.map((s) => s.id),
  );
  for (const sheet of singleLine) {
    const els = Object.values(project.elements).filter((e) => e.sheetId === sheet.id);
    const fed = new Set<Id>();

    for (const dev of els) {
      if (!PROTECTIVE_DEVICES.has(dev.symbolId)) continue;
      const def = getSymbol(dev.symbolId)!;
      const outPorts = def.ports.filter((p) => p.dir === 'down' || p.id.startsWith('out'));
      // ---- downstream
      const points: CircuitPoint[] = [];
      const seenNets = new Set<string>();
      const queue = outPorts.map((p) => net.netOf(dev.id, p.id));
      const cableLabels: string[] = [];
      while (queue.length) {
        const n = queue.shift()!;
        if (seenNets.has(n)) continue;
        seenNets.add(n);
        for (const w of net.wiresIn(n)) if (w.label) cableLabels.push(w.label);
        for (const { elementId, portId } of net.portsIn(n)) {
          if (elementId === dev.id) continue;
          const el = project.elements[elementId]!;
          const k = pointKind(el);
          const edef = getSymbol(el.symbolId);
          if (k) {
            if (!points.some((p) => p.element.id === el.id)) points.push({ element: el, kind: k });
            fed.add(el.id);
            continue;
          }
          if (!edef || PROTECTIVE_DEVICES.has(el.symbolId) || RCD_DEVICES.has(el.symbolId)) continue;
          // Pass-through devices (contactor, time switch, switch-disconnector…): continue if entered on the supply side.
          const entered = edef.ports.find((p) => p.id === portId);
          if (entered && (entered.id.startsWith('in') || entered.dir === 'up')) {
            for (const p of edef.ports)
              if (p.id !== portId && (p.id.startsWith('out') || p.dir === 'down'))
                queue.push(net.netOf(el.id, p.id));
          }
        }
      }
      points.sort((a, b) => a.element.y - b.element.y || a.element.x - b.element.x);

      // ---- upstream RCDs
      const rcds: Element[] = [];
      if (RCD_DEVICES.has(dev.symbolId)) rcds.push(dev);
      const upSeen = new Set<string>();
      const up = def.ports
        .filter((p) => p.id.startsWith('in') || p.dir === 'up')
        .map((p) => net.netOf(dev.id, p.id));
      while (up.length) {
        const n = up.shift()!;
        if (upSeen.has(n)) continue;
        upSeen.add(n);
        for (const { elementId, portId } of net.portsIn(n)) {
          if (elementId === dev.id) continue;
          const el = project.elements[elementId]!;
          const edef = getSymbol(el.symbolId);
          const port = edef?.ports.find((p) => p.id === portId);
          if (!edef || !port || !(port.id.startsWith('out') || port.dir === 'down')) continue;
          if (RCD_DEVICES.has(el.symbolId) && !rcds.includes(el)) rcds.push(el);
          for (const p of edef.ports)
            if (p.id.startsWith('in') || p.dir === 'up') up.push(net.netOf(el.id, p.id));
        }
      }

      const circuit = dev.circuitId ? project.circuits[dev.circuitId] : undefined;
      const parsed = parseCable(cableLabels[0]);
      const cable =
        circuit && circuit.cable.sectionMm2 > 0
          ? { ...circuit.cable, source: 'table' as const }
          : parsed
            ? { ...parsed, source: 'label' as const }
            : { type: '', sectionMm2: 0, cores: 0, source: 'none' as const };
      const info: CircuitInfo = {
        device: dev,
        circuit,
        code: circuit?.code ?? '',
        sheet,
        points,
        rcds,
        cable,
        ratingA: num(dev.props.ratingA),
      };
      circuits.push(info);
      for (const r of rcds) byRcd.set(r.id, [...(byRcd.get(r.id) ?? []), info]);
    }

    for (const el of els) {
      const def = getSymbol(el.symbolId);
      if (!def || def.ports.length === 0) continue;
      const connected = def.ports.some((p) => {
        const n = net.netOf(el.id, p.id);
        return net.wiresIn(n).length > 0 || net.portsIn(n).length > 1;
      });
      if (!connected) unconnected.push(el);
      else if (pointKind(el) && !fed.has(el.id)) unprotected.push(el);
    }
  }
  circuits.sort((a, b) => (a.code || '~').localeCompare(b.code || '~') || a.device.x - b.device.x);
  return { circuits, unprotected, unconnected, byRcd };
}

/** Sensitivity (mA) and type of the RCD protecting a circuit, nearest first. */
export function effectiveRcd(
  info: CircuitInfo,
): { sensitivityMa: number; type: string; element: Element } | undefined {
  const r = info.rcds[0];
  if (!r) return undefined;
  const def = getSymbol(r.symbolId);
  const f = (k: string) =>
    r.props[k] ?? (def?.fields?.find((x) => x.key === k) as { default?: unknown } | undefined)?.default;
  return { sensitivityMa: Number(f('sensitivityMa')), type: String(f('rcdType')), element: r };
}

/** Lowest sensitivity (most sensitive) RCD upstream. */
export function mostSensitiveRcd(info: CircuitInfo): number | undefined {
  const values = info.rcds.map((r) => {
    const def = getSymbol(r.symbolId);
    const d = (def?.fields?.find((x) => x.key === 'sensitivityMa') as { default?: unknown } | undefined)
      ?.default;
    // '' = unknown (unreadable on an imported photo).
    return r.props.sensitivityMa === '' ? NaN : Number(r.props.sensitivityMa ?? d);
  });
  const known = values.filter((v) => Number.isFinite(v));
  return known.length ? Math.min(...known) : undefined;
}
